import { Box, BoxProps, Button, Grid } from "@mui/material";
import { useStore } from "@tanstack/react-form";
import React, { lazy } from "react";
import { z } from "zod/v4";

import { Modal, ModalFooter, PasswordStrengthIndicator } from "src/components";
import { useAppForm } from "src/hooks";
import { isValidPassword } from "src/utils";

import CredentialsModificationInfo from "../CredentialsModificationInfo";

const ProfileCredentialsSuccess = lazy(() => import("../ProfileCredentialsUpdateSuccess"));

export interface UpdatePasswordInputs {
  newPassword: string;
  currentPassword: string;
}

interface Props extends Omit<BoxProps, "onSubmit"> {
  loading: boolean;
  open: boolean;
  isSuccess: boolean;
  handleClose: () => void;
  onSubmit: (data: UpdatePasswordInputs) => void;
}

export const ModalPassword = ({
  loading,
  isSuccess,
  open,
  handleClose,
  onSubmit,
  ...boxProps
}: Props) => {
  const form = useAppForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
    },
    validators: {
      onSubmit: z.object({
        currentPassword: z.string().min(1, { error: "Mot de passe actuel requis" }),
        newPassword: z.string(),
      }),
    },
    onSubmit: ({ value }) => {
      onSubmit({
        currentPassword: value.currentPassword,
        newPassword: value.newPassword,
      });
    },
  });

  const newPassword = useStore(form.store, (state) => state.values.newPassword);

  const handleResetForm = () => {
    form.reset();
    handleClose();
  };

  return (
    <Box component="form" {...boxProps}>
      <Modal
        icon="lock"
        title="Modifier mon mot de passe"
        open={open}
        handleClose={handleResetForm}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          {isSuccess ? (
            <ProfileCredentialsSuccess />
          ) : (
            <>
              <CredentialsModificationInfo />
              <Grid container gap={2}>
                <form.AppField name="currentPassword">
                  {(field) => (
                    <field.PasswordField
                      label="Mot de passe actuel"
                      inputProps={{ autoComplete: "current-password" }}
                    />
                  )}
                </form.AppField>

                <form.AppField name="newPassword">
                  {(field) => (
                    <field.PasswordField
                      label="Nouveau mot de passe"
                      inputProps={{
                        "aria-describedby": "password-strength-indicator",
                        autoComplete: "new-password",
                      }}
                    />
                  )}
                </form.AppField>
              </Grid>

              <PasswordStrengthIndicator password={newPassword} sx={{ mt: 2 }} />
            </>
          )}

          <ModalFooter>
            {isSuccess ? (
              <Button variant="contained" onClick={handleResetForm}>
                Fermer
              </Button>
            ) : (
              <>
                <form.SubmitButton loading={loading} disabled={!isValidPassword(newPassword)}>
                  Confirmer
                </form.SubmitButton>
              </>
            )}
          </ModalFooter>
        </form>
      </Modal>
    </Box>
  );
};
